import { createHouseSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { Notice, errorMessage, primaryButton } from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';

interface House {
  id: string;
  name: string;
  color: string | null;
}

export function HousesTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', color: '#1e3a8a' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const houses = useQuery({
    queryKey: ['houses', schoolId],
    queryFn: () => api<House[]>(`/schools/${schoolId}/houses`),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['houses', schoolId] });
  const create = useMutation({
    mutationFn: (body: object) => api(`/schools/${schoolId}/houses`, { method: 'POST', body }),
    onSuccess: () => {
      setForm({ name: '', color: '#1e3a8a' });
      setMessage(null);
      void refresh();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/schools/${schoolId}/houses/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e) => setMessage(errorMessage(e)),
  });

  return (
    <section aria-label={t('acad.tab.houses')} className="space-y-4">
      {message && <Notice kind="error">{message}</Notice>}
      {can('houses.create', schoolId) && (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = createHouseSchema.safeParse(form);
            if (!parsed.success)
              return setErrors(
                Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])),
              );
            setErrors({});
            create.mutate(parsed.data);
          }}
          className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4"
        >
          <TextField
            label={t('acad.name')}
            value={form.name}
            error={errors.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label={t('acad.colour')}
            type="color"
            value={form.color}
            error={errors.color}
            onChange={(e) => setForm({ ...form, color: e.target.value })}
          />
          <button type="submit" className={primaryButton}>
            {t('acad.addHouse')}
          </button>
        </form>
      )}
      {houses.data?.length === 0 && <p className="text-slate-600">{t('acad.noHouses')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {houses.data?.map((h) => (
          <li key={h.id} className="flex items-center justify-between p-3">
            <span className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block h-4 w-4 rounded-full border border-slate-300"
                style={{ backgroundColor: h.color ?? 'transparent' }}
              />
              <span className="font-medium">{h.name}</span>
            </span>
            {can('houses.delete', schoolId) && (
              <button
                type="button"
                className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                onClick={() =>
                  window.confirm(t('common.confirmDelete', { name: h.name })) && remove.mutate(h.id)
                }
              >
                {t('acad.delete')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
